import { createRef, FC, RefObject, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SplitIOTreatment from '../../../components/splitio/splitioTreatment';
import { Link as RouteLink } from '../../types';

const breakpoint = {
  medium: 'min-width: 1024px',
  large: 'min-width: 1280px',
};

const NavContainer = styled.ul(({ theme }) => {
  const { color } = theme;

  return `
    width: calc(100vw - 40px);
    padding: 0 16px;
    border-radius: 12px;
    box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.1);
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 5;
    background-color: ${color.grayscale[1000]};
    display: none;

    &.visible {
      display: block;
    }

    @media screen and (${breakpoint.medium}) {
      width: auto;
      right: 1rem;
      top: 2rem;
    }
  `;
});

const DropdownItem = styled.li(({ theme }) => {
  const { color } = theme;

  return `
    padding: 16px 0;

    &:not(:last-child) {
      border-bottom: 1px solid ${color.grayscale[700]};
    }

    @media screen and (min-width: 1200px) {
      &.mobile-only {
        display: none;
      }
    }
  `;
});

const DropdownLink = styled(Link)(({ theme }) => {
  const { color } = theme;
  return `
    &:hover {
      color: ${color.primary.regular};
      text-decoration: none;
    }
  `;
});

export interface IDropDownNav {
  links: RouteLink[];
  extended: boolean;
  toggleButtonRefs: RefObject<HTMLButtonElement>[];
  closeCallback: () => void;
}

const DropDownNav: FC<IDropDownNav> = ({ links, extended, toggleButtonRefs, closeCallback }) => {
  const dropDownRef = createRef<HTMLUListElement>();

  const handleOutsideClick = (e: MouseEvent) => {
    for (const ref of toggleButtonRefs) {
      if (ref?.current?.contains(e.target as Node)) {
        return;
      }
    }

    if (!dropDownRef?.current?.contains(e.target as Node) && extended) {
      closeCallback();
    }
  };

  const handleEscapePress = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && extended) {
      closeCallback();
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleOutsideClick);
    document.addEventListener('keyup', handleEscapePress);

    return () => {
      document.removeEventListener('mouseup', handleOutsideClick);
      document.addEventListener('keyup', handleEscapePress);
    };
  });

  return (
    <NavContainer
      role="menu"
      id="navDropdown"
      className={extended ? 'visible' : 'hidden'}
      aria-labelledby="navToggleButton"
      ref={dropDownRef}
    >
      {links.map((link, index) => {
        if (typeof link.featureToggle !== 'undefined') {
          return (
            <SplitIOTreatment
              featureName={link.featureToggle.name}
              trafficType={link.featureToggle.type}
              key={`dropdownNav-${index}`}
            >
              {(treatment: string) => {
                if (treatment === 'on' && link.to) {
                  return (
                    <DropdownItem className={link.placement === 2 ? 'mobile-only' : ''} role="none">
                      <DropdownLink role="menuitem" to={link.to} onClick={closeCallback}>
                        {link.label}
                      </DropdownLink>
                    </DropdownItem>
                  );
                } else {
                  return null;
                }
              }}
            </SplitIOTreatment>
          );
        }

        if (link.to) {
          return (
            <DropdownItem
              key={`dropdownNav-${link.to?.replace(/^\w+$/, '')}`}
              className={link.placement === 2 ? 'mobile-only' : ''}
              role="none"
            >
              <DropdownLink role="menuitem" to={link.to} onClick={closeCallback}>
                {link.label}
              </DropdownLink>
            </DropdownItem>
          );
        }
      })}
    </NavContainer>
  );
};

export default DropDownNav;
